import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import CaseText from "@/components/CaseText";
import { X, Library, Loader2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

// Vocabulary (key legal terms) loaded once and shared.
let VOCAB = null;
let VPROMISE = null;
function loadVocab() {
  if (VOCAB) return Promise.resolve(VOCAB);
  if (!VPROMISE) {
    VPROMISE = api.get("/dictionary")
      .then((r) => {
        VOCAB = Array.from(new Set(r.data.map((t) => t.term)))
          .filter((t) => t && t.length >= 4)
          .sort((a, b) => b.length - a.length);
        return VOCAB;
      })
      .catch(() => { VOCAB = []; return VOCAB; });
  }
  return VPROMISE;
}
const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export default function LinkedText({ text, className = "" }) {
  const [vocab, setVocab] = useState(VOCAB || []);
  const [modal, setModal] = useState(null);

  useEffect(() => { loadVocab().then(setVocab); }, []);

  const regex = useMemo(() => {
    if (!vocab.length) return null;
    return new RegExp(`\\b(${vocab.map(esc).join("|")})\\b`, "gi");
  }, [vocab]);

  const openDefine = async (word) => {
    setModal({ term: word, loading: true });
    try {
      const r = await api.get(`/blacks/define?term=${encodeURIComponent(word)}`);
      setModal({ term: r.data.term || word, definition: r.data.definition, loading: false });
    } catch {
      setModal({ term: word, notFound: true, loading: false });
    }
  };

  const nodes = useMemo(() => {
    if (!text) return null;
    if (!regex) return text;
    const out = [];
    let last = 0, m, key = 0;
    regex.lastIndex = 0;
    while ((m = regex.exec(text)) !== null) {
      if (m.index > last) out.push(text.slice(last, m.index));
      const word = m[1];
      out.push(
        <button key={`t-${key++}`} onClick={() => openDefine(word)} data-testid="term-link"
          className="text-primary/90 underline decoration-dotted decoration-primary/50 underline-offset-2 hover:text-primary hover:decoration-solid transition-colors">
          {word}
        </button>
      );
      last = m.index + word.length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, regex]);

  return (
    <>
      <span className={className}>{nodes}</span>
      {modal && createPortal(
        <div className="fixed inset-0 z-[95] bg-black/70 flex items-center justify-center p-4" onClick={() => setModal(null)} data-testid="define-modal">
          <div className="bg-card border border-border rounded-lg max-w-lg w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Library className="w-5 h-5 text-primary" />
                <h3 className="font-mono text-lg">{modal.term}</h3>
              </div>
              <button onClick={() => setModal(null)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 overflow-y-auto">
              {modal.loading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : modal.notFound ? (
                <p className="text-sm text-muted-foreground">No entry for “{modal.term}” in Black's Law 4th. Try the <Link to="/blacks" className="text-primary hover:underline">full dictionary</Link>.</p>
              ) : (
                <>
                  <p className="text-sm text-foreground/85 leading-relaxed"><CaseText text={modal.definition} /></p>
                  <p className="text-[10px] font-mono text-muted-foreground/70 mt-4">— Black's Law Dictionary (Rev. 4th ed., 1968)</p>
                </>
              )}
            </div>
            <div className="px-6 py-3 border-t border-border">
              <Link to="/blacks" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-primary hover:underline">
                Open Black's Law <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
